'use client';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Vehicle {
  id: string;
  name: string;
  category: string;
  description: string;
  price: string;
  capacity: string;
  features: string[];
  image: string;
  imageAlt: string;
  popular?: boolean;
}

const vehicles: Vehicle[] = [
{
  id: 'vehicle_basic',
  name: 'Basic Scooter',
  category: 'Standard',
  description: 'Perfect for short city trips',
  price: '$3 unlock + $0.25/min',
  capacity: '1 rider',
  features: ['15 mph max speed', 'LED lights', '15 mile range'],
  image: "https://images.unsplash.com/photo-1698403604423-8c2c1a06bc7b",
  imageAlt: 'Black electric scooter with LED display and front basket'
},
{
  id: 'vehicle_premium',
  name: 'Premium Scooter',
  category: 'Enhanced',
  description: 'Extra power and comfort',
  price: '$4 unlock + $0.35/min',
  capacity: '1 rider',
  features: ['20 mph max speed', 'Suspension', '25 mile range'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_165fe94d1-1767510456105.png",
  imageAlt: 'Red premium electric scooter with dual suspension and digital display',
  popular: true
},
{
  id: 'vehicle_cargo',
  name: 'Cargo Scooter',
  category: 'Heavy Duty',
  description: 'Built for carrying items',
  price: '$5 unlock + $0.40/min',
  capacity: '1 rider + cargo',
  features: ['18 mph max speed', 'Large basket', '20 mile range'],
  image: "https://images.unsplash.com/photo-1714550454721-a7f6624c09d2",
  imageAlt: 'White cargo electric scooter with large front basket and storage compartment'
}];


export default function VehicleGrid() {
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  return (
    <section className="py-24 bg-gradient-to-b from-muted/30 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <span className="inline-block px-4 py-2 rounded-full bg-secondary/10 text-secondary text-sm font-body font-semibold mb-4">
            Scooter Options
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
            Choose Your Scooter
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            From basic commutes to premium rides, we have the perfect electric scooter for every journey.
          </p>
        </div>

        {/* Vehicle Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {vehicles.map((vehicle, index) =>
          <div
            key={vehicle.id}
            className={`card group cursor-pointer relative overflow-hidden reveal reveal-delay-${index * 100} ${
            selectedVehicle === vehicle.id ? 'ring-2 ring-accent' : ''}`
            }
            onClick={() => setSelectedVehicle(vehicle.id)}>
            
              {/* Popular Badge */}
              {vehicle.popular &&
            <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-body font-bold">
                  Most Popular
                </div>
            }

              {/* Vehicle Image */}
              <div className="relative h-48 mb-4 rounded-lg overflow-hidden bg-muted">
                <AppImage
                src={vehicle.image}
                alt={vehicle.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                
                {/* Price Overlay */}
                <div className="absolute bottom-4 left-4 px-3 py-1 rounded-lg glass-dark">
                  <p className="text-white font-display font-bold">{vehicle.price}</p>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-display font-bold text-primary mb-1">
                      {vehicle.name}
                    </h3>
                    <p className="text-sm text-accent font-body font-semibold">
                      {vehicle.category}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Icon name="BoltIcon" size={20} className="text-secondary" />
                  </div>
                </div>

                <p className="text-muted-foreground font-body text-sm">
                  {vehicle.description}
                </p>

                {/* Capacity */}
                <div className="flex items-center gap-2 text-sm text-foreground/70">
                  <Icon name="UserIcon" size={16} className="text-muted-foreground" />
                  <span className="font-body">{vehicle.capacity}</span>
                </div>

                {/* Features */}
                <div className="space-y-2 pt-2 border-t border-border">
                  {vehicle.features.map((feature, idx) =>
                <div key={`${vehicle.id}_feature_${idx}`} className="flex items-center gap-2">
                      <Icon name="CheckCircleIcon" size={16} className="text-success" />
                      <span className="text-sm text-foreground/70 font-body">{feature}</span>
                    </div>
                )}
                </div>

                {/* CTA */}
                <button className="w-full mt-4 py-2 rounded-lg bg-muted hover:bg-accent hover:text-accent-foreground transition-all font-body font-semibold text-sm">
                  Select {vehicle.name}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12 reveal reveal-delay-300">
          <a
            href="/booking"
            className="inline-flex items-center gap-2 text-secondary hover:text-primary transition-colors font-body font-semibold">
            
            View All Scooter Options
            <Icon name="ArrowRightIcon" size={20} />
          </a>
        </div>
      </div>
    </section>);

}